/**
 * TechHub (ADR-0022): the client list for the companies where TechHub is on
 * and this person has a TechHub role. Follows the company switcher.
 */
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, type ChClient } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { fmt, parseTs } from "../../lib/format";
import { Loading, Tile } from "../../components/bits";
import { timeOf, useSort } from "../../components/Sort";
import { Icon } from "../../components/Icon";
import { ServicePicker } from "./ServicePicker";
import { SeeHow } from "../../components/SeeHow";

export const STATUS_LABEL: Record<string, [string, string]> = {
  onboarding: ["Onboarding", "info"], active: ["Active", "pub"], inactive: ["Inactive", "draft"], former: ["Former client", "draft"],
};
export const clientInitials = (name: string) => name.split(/\s+/).filter((w) => /[A-Za-z0-9]/.test(w[0] ?? "")).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
/** A steady color per client (from its code), from TechHub's data palette: no more all-brown tiles. */
const CLIENT_COLORS = ["#2F6FB3", "#1F9E8F", "#7C5CC4", "#D9486B", "#C2410C", "#17786C", "#B7791F", "#3B82C4", "#9B3D8F", "#2F9E5B"];
export const clientColor = (code: string) => CLIENT_COLORS[[...code].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % CLIENT_COLORS.length]!;
/** Stale: not verified (or never) in 6 months. */
export const isStale = (iso: string | null) => { const d = parseTs(iso); return !d || Date.now() - d.getTime() > 182 * 864e5; };

export function ClientsPage() {
  const { p, ws, toast } = useStore();
  const nav = useNavigate();
  const [rows, setRows] = useState<ChClient[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const codes = p.chCodes.filter((c) => ws === "ALL" || c === ws);
  const load = () => api.chClients(ws).then((r) => setRows(r.clients), (e: Error) => setErr(e.message));
  useEffect(() => { setRows(null); setErr(null); if (codes.length) void load(); }, [ws]);
  const { sorted: sortedRows, th } = useSort("ch.clients", rows ?? [], { name: (r) => r.name, company: (r) => r.company, primary: (r) => r.primary_name ?? "", sites: (r) => r.sites,
    status: (r) => r.status, verified: (r) => timeOf(r.last_verified_at) }, { key: "name", dir: "asc" });
  if (!p.chCodes.length) return <Navigate to="/" replace />;
  if (!codes.length) return <div className="card empty view"><h3>TechHub isn't on for {coName(ws)}</h3>Switch to a company that has it, or ask a platform admin to turn it on in Settings &gt; TechHub access.</div>;
  if (err) return <div className="card empty view"><h3>Clients didn't load</h3>{err}</div>;
  if (!rows) return <Loading />;
  const editCodes = codes.filter((c) => p.ch(c).edit);
  const shown = sortedRows.filter((r) => !q || `${r.name} ${r.code} ${r.primary_name ?? ""} ${r.services}`.toLowerCase().includes(q.toLowerCase()));
  const active = rows.filter((r) => r.status === "active").length;
  const stale = rows.filter((r) => r.status !== "former" && isStale(r.last_verified_at)).length;

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">TechHub · {ws === "ALL" ? "All my companies" : coName(ws)}</div>
          <h1 className="title">Clients</h1>
          <div className="lede">Everything about each client in one place. Documents stay in the Vault; TechHub links to them.</div>
        </div>
        {editCodes.length > 0 && <div className="row-btns"><SeeHow video="add-client" /><button className="btn btn-accent" onClick={() => setAdding((a) => !a)}><Icon name="plus" />New client</button></div>}
      </div>

      {adding && <NewClient codes={editCodes} onDone={(id) => { setAdding(false); toast("Client added."); nav(`/clienthub/c/${id}`); }} onCancel={() => setAdding(false)} />}

      <div className="kpis k5">
        <div className="kc" style={{ ["--kc" as string]: "#2F6FB3", cursor: "default" }}><span className="ic"><Icon name="users" /></span><span className="v">{active}</span><span className="l">Active clients</span></div>
        <div className="kc" style={{ ["--kc" as string]: "#1F9E8F", cursor: "default" }}><span className="ic"><Icon name="dash" /></span><span className="v">{rows.reduce((n, r) => n + Number(r.asset_count ?? 0), 0)}</span><span className="l">Assets</span></div>
        <button type="button" className="kc kc-btn" style={{ ["--kc" as string]: "#E0A31A" }} onClick={() => nav("/clienthub/agreements")} title="Open all agreements"><span className="ic"><Icon name="sign" /></span><span className="v">{rows.reduce((n, r) => n + Number(r.agreement_alerts ?? 0), 0)}</span><span className="l">Agreements due (90 days)</span></button>
        <div className="kc" style={{ ["--kc" as string]: "#D9486B", cursor: "default" }}><span className="ic"><Icon name="clock" /></span><span className="v">{rows.reduce((n, r) => n + Number(r.warranty_alerts ?? 0), 0)}</span><span className="l">Warranty alerts (90 days)</span></div>
        <div className="kc" style={{ ["--kc" as string]: "#7C5CC4", cursor: "default" }}><span className="ic"><Icon name="clock" /></span><span className="v">{stale}</span><span className="l">Not verified (6 months)</span></div>
      </div>

      <div className="filters">
        <label className="inline-search"><Icon name="search" size={16} /><input placeholder="Find a client or contact" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a client" /></label>
        <span className="cap">{shown.length} of {rows.length} clients</span>
      </div>

      {rows.length === 0 ? (
        <div className="card empty"><h3>No clients yet</h3>{editCodes.length ? "Add the first one with New client." : "A TechHub editor or admin adds clients."}</div>
      ) : (
        <div className="card tbl-wrap">
          <table className="tbl">
            <thead><tr>{th("name", "Client")}{codes.length > 1 && th("company", "Company", "hide-md")}{th("primary", "Primary contact")}{th("sites", "Sites", "hide-md")}<th className="hide-md">Services</th>{th("status", "Status")}{th("verified", "Last verified")}</tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className="click" onClick={() => nav(`/clienthub/c/${r.id}`)}>
                  <td><div className="cl-name"><span className="cl-tile" style={{ background: clientColor(r.code) }}>{clientInitials(r.name)}</span><div><b>{r.name}</b><small>{r.code}</small></div></div></td>
                  {codes.length > 1 && <td className="hide-md"><Tile code={r.company} /></td>}
                  <td>{r.primary_name ?? <span className="cap">Not set</span>}{r.primary_title && <div className="cap">{r.primary_title}</div>}</td>
                  <td className="hide-md">{r.sites}</td>
                  <td className="hide-md">{r.service_tags?.length ? <span className="svc-tags">{r.service_tags.slice(0, 3).map((t) => <span key={t} className="svc-tag">{t}</span>)}{r.service_tags.length > 3 && <span className="svc-tag more">+{r.service_tags.length - 3}</span>}</span> : <span className="cap">Not set</span>}</td>
                  <td><span className={`pill ${STATUS_LABEL[r.status]?.[1] ?? "draft"} sm`}>{STATUS_LABEL[r.status]?.[0] ?? r.status}</span></td>
                  <td style={isStale(r.last_verified_at) && r.status !== "former" ? { color: "var(--warn)", fontWeight: 600 } : undefined}>{r.last_verified_at ? fmt(r.last_verified_at) : "Never"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewClient({ codes, onDone, onCancel }: { codes: string[]; onDone: (id: string) => void; onCancel: () => void }) {
  const { toast } = useStore();
  const [company, setCompany] = useState(codes[0]!);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [status, setStatus] = useState("active");
  const [busy, setBusy] = useState(false);
  const suggest = (n: string) => n.split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).join("").replace(/[^A-Z0-9]/g, "").slice(0, 4);
  const ok = name.trim().length >= 2 && /^[A-Z0-9]{2,8}$/.test(code);
  const save = async () => {
    setBusy(true);
    try { const r = await api.chCreateClient({ company, name: name.trim(), code, service_ids: serviceIds, status: status as "active" }); onDone(r.id); }
    catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="card sect" style={{ marginBottom: 16 }}>
      <h3>New client</h3>
      <div className="form-row">
        {codes.length > 1 && <label><span className="lbl">Company</span><select className="inp" value={company} onChange={(e) => { setCompany(e.target.value); setServiceIds([]); }}>{codes.map((c) => <option key={c} value={c}>{coName(c)}</option>)}</select></label>}
        <label className="grow"><span className="lbl">Name</span><input className="inp" value={name} maxLength={120} autoFocus onChange={(e) => { setName(e.target.value); if (!code || code === suggest(name)) setCode(suggest(e.target.value)); }} placeholder="e.g. Harbor Point Dental" /></label>
        <label><span className="lbl">Code</span><input className="inp mono" value={code} maxLength={8} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="HPD" style={{ width: 110 }} /></label>
        <label><span className="lbl">Status</span><select className="inp" value={status} onChange={(e) => setStatus(e.target.value)}>{Object.entries(STATUS_LABEL).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select></label>
      </div>
      <div className="field"><span className="lbl">Services</span><ServicePicker company={company} value={serviceIds} onChange={setServiceIds} /></div>
      <div className="row-btns"><button className="btn btn-accent" disabled={!ok || busy} onClick={() => void save()}>Add client</button><button className="btn btn-ghost" onClick={onCancel}>Cancel</button></div>
      <div className="help">The code is short and unique in the company (2 to 8 capitals or digits). One record per company: a client served by two companies gets a record in each.</div>
    </div>
  );
}
